<template>
	<form ref="contactform" method="post" enctype="multipart/form-data" class="contact-form" @submit.prevent="submit()">
		<span v-if="state.error" class="error">
			{{ $t(`form${capitalize(state.error)}`) }}
		</span>
		<div :class="['formbody', { wrap: singleColumn }]">
			<div class="left-form-column">
				<div v-for="field in form" :key="field.name">
					<div
						v-if="
							field.name == 'name' ||
							field.name == 'companyName' ||
							field.name == 'email' ||
							field.name == 'phone'
						"
					>
						<input
							:key="field.name"
							v-model="field.value"
							:name="field.name"
							:required="field.required"
							:placeholder="$t(`form${capitalize(field.name)}`)"
							:type="state.inputTypes[field.name] || 'text'"
						/>
					</div>
					<div v-else-if="field.edittype == 'fileuploader'">
						<label :for="field.name" class="fileupload-group">
							<span class="label-text">{{ $t('formCV') }}<span v-if="field.required"> *</span></span>
							<div class="dropzone">
								<div class="dropzone-icon">
									<font-awesome-icon :icon="['fas', 'upload']" />
								</div>
								<input
									:id="field.name"
									:key="field.name"
									type="file"
									accept=".doc,.docx,.pdf,.odt"
									:name="field.name"
									:required="field.required"
									@change="onFileChange"
								/>
							</div>
						</label>
					</div>
					<div v-else-if="field.edittype == 'textarea'">
						<textarea
							:key="field.name"
							v-model="field.value"
							:name="field.name"
							:placeholder="$t(`form${capitalize(field.name)}`)"
							:required="field.required"
						/>
					</div>
				</div>
			</div>
			<div class="right-form-column">
				<div class="right-top-row">
					<div v-for="field in form.filter((f) => ['eventDate', 'pax'].includes(f.name))" :key="field.name">
						<client-only v-if="field.name === 'eventDate'">
							<date-picker
								v-model:value="state.eventDate"
								value-type="format"
								format="DD-MM-YYYY"
								:lang="datepickerLang"
								:placeholder="$t(`form${capitalize(field.name)}`)"
							>
							</date-picker>
						</client-only>

						<template v-else-if="field.name === 'pax'">
							<div class="input-with-icon">
								<input
									v-model="field.value"
									:name="field.name"
									:required="field.required"
									:placeholder="$t(`form${capitalize(field.name)}`)"
									:type="state.inputTypes[field.name] || 'text'"
								/>
							</div>
						</template>
					</div>
				</div>

				<div v-for="field in form" :key="field.name">
					<div v-if="field.name == 'eventType'" class="formgroup-container">
						<div class="formgroup-heading">
							{{ $t(`form${capitalize(field.name)}`) }}
							<span class="heading-icon">
								<div class="meeting-type-icon" />
							</span>
						</div>

						<div v-for="(key, value) in field.options" :key="key" class="checkboxes">
							<label>
								<input
									:id="field.name"
									v-model="state.eventTypes"
									type="checkbox"
									:name="field.name"
									:value="$t(`form${capitalize(value)}`)"
								/>
								{{ $t(`form${capitalize(value)}`) }}
							</label>
						</div>
						<div class="checkboxes block">
							<label>
								<input
									v-model="state.eventTypeOtherEnabled"
									name="otherSpecify"
									type="checkbox"
									class="toggle-extra-option"
								/>{{ $t('formOtherSpecify') }}
							</label>
							<input
								v-model="state.eventTypeOther"
								type="text"
								class="extra-option"
								:disabled="!state.eventTypeOtherEnabled"
							/>
						</div>
					</div>

					<div v-else-if="field.edittype == 'radiogroup'" class="formgroup-container">
						<div class="formgroup-heading">
							{{ $t(`form${capitalize(field.name)}`) }}
						</div>
						<div class="radiobuttons">
							<div v-for="(value, key) in field.options" :key="key">
								<label>
									<input v-model="field.value" type="radio" :name="field.name" :value="key" />{{
										$t(`form${capitalize(key)}`)
									}}
								</label>
							</div>
							<div v-if="!field.required">
								<label>
									<input type="radio" :name="field.name" value="" />{{ $t('formNoPreference') }}
								</label>
							</div>
						</div>
					</div>

					<div v-else-if="field.edittype == 'list'" class="select">
						<select :id="field.name" v-model="field.value" :name="field.name">
							<option v-if="!field.required" :value="null" selected>
								{{ $t(`form${capitalize(field.name)}`) }}
							</option>
							<option v-for="(value, key) in field.options" :key="key" :value="value">
								{{ !field.valueIsAbsolute ? $t(`form${capitalize(key)}`) : value }}
							</option>
						</select>
					</div>
				</div>
			</div>
		</div>

		<div class="consent-form-column">
			<div v-for="field in form" :key="field.name">
				<div v-if="field.edittype == 'checkbox'" class="formgroup-container">
					<div class="checkboxes">
						<label :for="field.name">
							<input
								:id="field.name"
								v-model="field.value"
								:name="field.name"
								type="checkbox"
								:checked="!!field.value"
								:placeholder="$t(`form${capitalize(field.name)}`)"
								:required="field.required"
							/>
							<span>{{ $t(`form${capitalize(field.name)}`) }}</span>
						</label>
					</div>
				</div>
			</div>
		</div>

		<div class="submit">
			<input type="submit" class="button button-cta action" :value="$t('formSubmit')" />
		</div>
	</form>
</template>

<script setup>
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import datePickerTranslations from '~/i18n/datePicker.ts';

const { locale } = useI18n();
const { $installRecaptcha, $executeRecaptcha } = useNuxtApp();
const { t } = useI18n();
const config = useRuntimeConfig();

const contactform = ref(null);

const props = defineProps({
	extraQuerydata: { type: Object, default: () => {} },
	form: { type: Array, default: () => [] },
	name: { type: String, default: 'contact' },
	singleColumn: { type: Boolean, default: false },
});

const state = useState('state', () => ({
	CV: '',
	eventDate: '',
	eventTypes: [],
	eventTypeOtherEnabled: false,
	eventTypeOther: '',
	inputTypes: { email: 'email', phone: 'tel' },
	error: '',
}));

const datepickerLang = computed(() => datePickerTranslations[locale.value] ?? datePickerTranslations.en);

const createStateData = (state) => {
	const stateData = {
		eventDate: state.eventDate,
		eventType: state.eventTypes.join(', '),
	};

	if (state.eventTypeOther) {
		stateData.eventType = `${stateData.eventType}, ${t('formOtherSpecify')} ${state.eventTypeOther}`;
	}

	return stateData;
};

const submit = async () => {
	const formData = new FormData();

	props.form.forEach((item) => {
		const value = item.value === 'null' ? '' : item.value;
		formData.append(item.name, value || '');
	});

	getFieldsByEditType('fileuploader').forEach((item) => {
		if (state.value[item.name]) {
			formData.set(item.name, state.value[item.name]);
		}
	});

	const stateData = createStateData(state.value);
	Object.entries(stateData).forEach(([key, value]) => {
		formData.append(key, value);
	});

	if (window.recaptcha) {
		formData.append('g-recaptcha-response', await $executeRecaptcha());
	}

	const result = await $fetch(config.public.apiBaseURL + '/' + props.name, {
		method: 'POST',
		body: formData,
		query: { language: locale.value, ...props.extraQuerydata },
	});

	if (result.status === 'error') {
		state.value.error = result.error;
	} else if (result.redirect) {
		contactform.value.reset();
		navigateTo(result.redirect);
	}
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const onFileChange = (e) => {
	const file = e.target.files[0] ?? null;
	if (file) {
		state.value.CV = file;
	}
};

const getFieldsByEditType = (edittype) => {
	return props.form.filter((item) => item.edittype === edittype);
};

onMounted(async () => {
	$installRecaptcha();
});
</script>

<style lang="scss" scoped>
.contact-form {
	margin: 0 auto;
	padding: 20px;
	background: var(--header-color);
	border-radius: 0;
	position: relative;
	height: 100%;

	.input-with-icon {
		position: relative;
	}

	.input-with-icon input {
		width: 100%;
		color: #fff;
		background: url('~/assets/images/users.svg') no-repeat;
		background-repeat: no-repeat;
		background-position: right 0 center;
		background-size: 20px;
	}

	.formbody {
		flex: 1;
		display: flex !important;
		align-items: stretch;
		flex-flow: row;
		justify-content: space-between;
		gap: 20px;
		padding-bottom: 40px;
		height: 100%;

		.left-form-column {
			display: flex;
			flex-flow: column;
			height: 100%;
			flex: 1 auto;
		}

		.left-form-column,
		.right-form-column {
			width: 100%;
			margin-left: 10px !important;

			.right-top-row {
				display: flex;
				align-items: end;
				gap: 20px;
				height: 83px;

				> * {
					flex: 1 1 50%;
				}
			}
		}
	}

	.consent-form-column {
		margin-left: 10px !important;
	}

	.formbody.wrap {
		flex-flow: row wrap !important;

		.right-form-column {
			display: none;
		}
	}
}

.mx-datepicker {
	width: 100%;
}

:deep(.mx-datepicker i) {
	color: var(--cta-color);
	top: 30px;
	right: 0;
}

form {
	margin: 40px 0 0;
	background: var(--header-color);

	input {
		padding: 20px 0;
	}

	.error {
		display: block;
		margin: 0 0 10px;
	}

	input:not([type='checkbox'], [type='radio'], [type='submit']),
	:deep(.mx-input),
	select,
	textarea,
	.fileupload-group {
		color: #fff;
		background: var(--header-color);
		width: 100%;
		max-width: 100%;
		padding: 20px 0;
		border: none;
		margin: 0 0 25px;
		font-size: 13px;
		font-family: var(--body-font-family);
		border-bottom: 1px solid #fff;
		border-radius: 0;
	}

	.formgroup-container .formgroup-heading,
	textarea::placeholder,
	input:not([type='checkbox'], [type='radio'], [type='submit'])::placeholder,
	:deep(.mx-input)::placeholder,
	select {
		opacity: 1;
		font-family: var(--body-font-family);
		margin-bottom: 15px;
		color: #fffbf6;
		font-size: 16px;
		font-weight: 200;
		width: 100%;
	}

	.formgroup-container .formgroup-heading {
		margin-bottom: 15px;
		display: flex;
		justify-content: space-between;
		position: relative;
		padding-right: 40px;

		.meeting-type-icon {
			position: absolute;
			top: 50%;
			right: 0;
			transform: translateY(-50%);
			width: 20px;
			height: 20px;
			background: url('~/assets/images/keynote.svg') no-repeat;
			background-position: center;
			background-size: contain;
		}
	}

	input:not([disabled], [type='submit']),
	select,
	textarea,
	:deep(.mx-input),
	.fileupload-group {
		background: var(--header-color);
	}

	select {
		background: url('~/assets/images/chevron-down.svg') no-repeat var(--header-color);
		background-position: right 0 top 50%;
		appearance: none;
	}

	input[type='submit'] {
		width: auto;
	}

	textarea {
		flex: 1 1 auto;
		min-height: 140px;
	}

	:deep(.mx-input) {
		height: auto;

		&:focus {
			outline: revert;
		}
	}

	.submit {
		width: 100%;
		background: var(--primary-background-color);
		position: absolute;
		left: 0;
		bottom: 0;
		display: flex;
		justify-content: end;
		border-radius: 0;
	}

	.button {
		width: auto;
		appearance: none;
		cursor: pointer;
		outline: none;
		border: none;
		padding: 20px 25px !important;
		margin: 0;
		border-radius: 0;
	}

	.fileupload-group {
		position: relative;
		display: block;
		cursor: pointer;
		color: #c7c4c0;

		.dropzone {
			display: flex;
			margin: 15px 0 0;
			overflow: hidden;

			input {
				cursor: pointer;
				max-width: 100%;
				border: none;
				margin-bottom: 0;
				padding: 0;
				color: #c7c4c0;
			}

			::-webkit-file-upload-button,
			::file-selector-button {
				display: none;
			}

			.dropzone-icon {
				margin-right: 10px;
			}
		}

		.label-text {
			display: block;
			text-align: left;
			font-size: 16px;
		}
	}

	.formgroup-container {
		position: relative;
		margin: 0 0 65px;
		display: flex;
		flex-flow: row wrap;
		gap: 10px;

		.checkboxes {
			display: flex;
			margin: 5px 0;

			label {
				color: #fff;
				width: auto;
				padding: 0;
				font-size: 0.9em;
				margin: 0 5px 0 0;
				display: flex;
				gap: 15px;
				align-items: center;

				input {
					width: 20px;
					padding: 0;
					appearance: auto;
					margin: -2px 6px 0 0;
				}
			}

			input[type='checkbox'] {
				appearance: none;
				width: 16px;
				height: 16px;
				border: 1px solid #fff;
				background-color: transparent;
				position: relative;
				cursor: pointer;
				display: inline-block;
				vertical-align: middle;
				margin: 0;
				box-sizing: border-box;
				border-radius: 4px;
				line-height: 1;
				padding: 7px !important;
			}

			/* Custom checkmark */
			input[type='checkbox']:checked::after {
				content: '';
				position: absolute;
				top: 0;
				left: 3px;
				width: 6px;
				height: 8px;
				border: solid #0e1a36;
				border-width: 0 2px 2px 0;
				transform: rotate(45deg);
			}

			/* White background when checked */
			input[type='checkbox']:checked {
				background-color: #fff;
			}

			.extra-option {
				position: absolute;
				left: 0;
				margin: 20px 0 0;
				width: 100%;
			}
		}

		.radiobuttons {
			display: flex;
			flex-flow: row wrap;
			margin: 5px 0 10px;
			max-width: 100%;

			> div {
				width: 40%;
				margin: 0 0 10px;
			}

			label,
			input {
				width: auto;
				padding: 0;
				font-size: 0.9em;
			}

			label {
				margin: 0 5px 0 0;
				display: flex;
				gap: 10px;

				input {
					width: auto;
					appearance: auto;
					margin: -2px 6px 0 0;
					padding: 0;
				}
			}
		}
	}
}

@media (max-width: 900px) {
	.formbody {
		flex-flow: row wrap !important;
	}
}

@media (max-width: 600px) {
	.formbody {
		.right-form-column {
			.right-top-row {
				display: block !important;
				height: unset !important;

				> * {
					flex: 1 1 100%;
				}
			}
		}
	}
}
</style>
